import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { getSourceUrls } from '../../services/sourceService';
import {
  deleteUrl,
  processUrl,
  testUrl,
  updateUrlActive,
  updateUrlFullScrapeRequired,
} from '../../services/urlService';
import JsonView from '../common/JsonView';
import { UrlItem } from '../../../../models';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  Spinner,
  Toggle,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  Input,
} from '../ui';
import { Bug, Play, Search as SearchIcon, Trash2 } from '../ui/icons';

interface ISourceUrlsListProps {
  sourceId: string;
}

interface IParseResult {
  success: boolean;
  message: string;
  propertyData?: any;
  error?: string;
}

const SourceUrlsList: React.FC<ISourceUrlsListProps> = ({ sourceId }) => {
  const [urls, setUrls] = useState<UrlItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [urlToDelete, setUrlToDelete] = useState<UrlItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);
  const [fullScrapeUpdating, setFullScrapeUpdating] = useState<string | null>(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [urlToTest, setUrlToTest] = useState<UrlItem | null>(null);
  const [testResult, setTestResult] = useState<IParseResult | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [urlSourceFilter, setUrlSourceFilter] = useState<'ALL' | 'SITEMAP' | 'MANUAL'>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDisabled, setShowDisabled] = useState(false);

  const loadUrls = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getSourceUrls(
        sourceId,
        undefined,
        showDisabled ? undefined : true
      );
      setUrls(response);
    } catch (err) {
      console.error('Error loading URLs:', err);
      setError('Failed to load URLs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sourceId, showDisabled]);

  useEffect(() => {
    loadUrls();
  }, [loadUrls]);

  const handleDeleteClick = (url: UrlItem) => {
    setUrlToDelete(url);
    setDeleteDialogOpen(true);
  };

  const handleToggleUrlActive = async (url: UrlItem) => {
    try {
      // Update the URL's active status
      await updateUrlActive(sourceId, url.urlHash, !url.active);
      // Reload URLs to get updated status
      await loadUrls();
    } catch (err) {
      console.error('Error updating URL active status:', err);
      setError('Failed to update URL status. Please try again.');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!urlToDelete) return;

    try {
      setDeleting(true);
      await deleteUrl(sourceId, urlToDelete.urlHash);
      setUrls(urls.filter(u => u.urlHash !== urlToDelete.urlHash));
      setDeleteDialogOpen(false);
    } catch (err: any) {
      console.error('Error deleting URL:', err);
      // Check if it's a 403 error (forbidden - sitemap URL)
      if (err.response?.status === 403) {
        setError('Cannot delete URLs that were added by sitemap. These URLs are managed automatically and may be re-added during the next sitemap processing job.');
      } else {
        setError('Failed to delete URL. Please try again.');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleProcessUrl = async (url: UrlItem) => {
    try {
      setProcessing(url.urlHash);
      await processUrl(sourceId, url.urlHash);
      // Reload URLs to get updated status
      await loadUrls();
    } catch (err) {
      console.error('Error processing URL:', err);
      setError('Failed to process URL. Please try again.');
    } finally {
      setProcessing(null);
    }
  };

  const handleToggleFullScrape = async (url: UrlItem, value: boolean) => {
    try {
      setFullScrapeUpdating(url.urlHash);
      await updateUrlFullScrapeRequired(sourceId, url.urlHash, value);
      await loadUrls();
    } catch (err) {
      console.error('Error updating full scrape flag:', err);
      setError('Failed to update full scrape flag. Please try again.');
    } finally {
      setFullScrapeUpdating(null);
    }
  };

  const handleTestUrl = (url: UrlItem) => {
    setUrlToTest(url);
    setTestResult(null);
    setTestDialogOpen(true);
  };

  const runUrlTest = async () => {
    if (!urlToTest) return;

    try {
      setTestLoading(true);
      const response = await testUrl(sourceId, urlToTest.sourceUrl);
      setTestResult({
        success: true,
        message: response.message,
        propertyData: response.propertyData
      });
    } catch (err: any) {
      console.error('Error testing URL:', err);
      setTestResult({
        success: false,
        message: 'Failed to test URL',
        error: err.response?.data?.message || 'Unknown error occurred'
      });
    } finally {
      setTestLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Badge variant="warning" size="sm">Pending</Badge>;
      case 'PROCESSED':
        return <Badge variant="success" size="sm">Processed</Badge>;
      case 'PROCESSING':
        return <Badge variant="default" size="sm">Processing</Badge>;
      case 'FAILED':
        return <Badge variant="error" size="sm">Failed</Badge>;
      case 'FILTERED':
        return <Badge variant="warning" size="sm">Filtered</Badge>;
      default:
        return <Badge variant="default" size="sm">{status}</Badge>;
    }
  };

  const formatDateTime = (value?: string) => (
    value ? new Date(value).toLocaleString() : 'Never'
  );

  const formatFallbackPropertyTitle = (sourceUrl: string) => {
    try {
      const { pathname } = new URL(sourceUrl);
      const ignoredSegments = new Set([
        'property',
        'property-for-sale',
        'properties-for-sale',
        'property-to-rent',
        'properties-to-rent',
      ]);
      const candidate = pathname
        .split('/')
        .filter(Boolean)
        .filter((segment) => !ignoredSegments.has(segment))
        .sort((a, b) => b.length - a.length)[0];

      if (!candidate) {
        return 'Property';
      }

      return candidate
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    } catch {
      return 'Property';
    }
  };

  const filteredUrls = urls.filter(url => {
    if (!showDisabled && url.active === false) {
      return false;
    }

    const matchesSource = urlSourceFilter === 'ALL' || url.urlSource === urlSourceFilter;
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const matchesSearch = !normalizedSearch ||
      url.sourceUrl.toLowerCase().includes(normalizedSearch) ||
      (url.propertyTitle || '').toLowerCase().includes(normalizedSearch) ||
      url.status.toLowerCase().includes(normalizedSearch) ||
      url.urlSource.toLowerCase().includes(normalizedSearch) ||
      (url.errorMessage || '').toLowerCase().includes(normalizedSearch);

    return matchesSource && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-[auto,minmax(20rem,28rem),auto] xl:items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Source</label>
          <ToggleButtonGroup value={urlSourceFilter} onChange={(value) => setUrlSourceFilter(value as any)}>
            <ToggleButton value="ALL">All URLs</ToggleButton>
            <ToggleButton value="SITEMAP">Sitemap URLs</ToggleButton>
            <ToggleButton value="MANUAL">Manual URLs</ToggleButton>
          </ToggleButtonGroup>
        </div>
        <Input
          label="Search URLs"
          value={searchTerm}
          onChange={(event) => setSearchTerm((event.target as HTMLInputElement).value)}
          placeholder="Search by URL, status, or source"
          startIcon={<SearchIcon className="h-4 w-4" />}
          fullWidth
        />
        <div className="pb-2">
          <Toggle
            label="Show disabled"
            checked={showDisabled}
            onChange={(event) => setShowDisabled(event.target.checked)}
          />
        </div>
      </div>
      <div className="mb-3 text-sm text-gray-500">
        Showing {filteredUrls.length} {showDisabled ? 'URL' : 'active URL'}
        {filteredUrls.length === 1 ? '' : 's'}
        {!showDisabled && urls.length > filteredUrls.length
          ? ` after filters`
          : ''}
      </div>

      {filteredUrls.length === 0 ? (
        <p className="text-gray-600 text-center py-8">
          {showDisabled
            ? 'No URLs found for this source. Add URLs to get started.'
            : 'No active URLs found for this source. Turn on "Show disabled" to include disabled URLs.'}
        </p>
      ) : (
        <div className="space-y-3">
          {filteredUrls.map((url) => {
            const propertyTitle = url.propertyTitle || formatFallbackPropertyTitle(url.sourceUrl);

            return (
              <article
                key={url.urlHash}
                className={`rounded-md border border-gray-200 bg-white p-4 shadow-sm ${
                  url.active === false ? 'border-gray-300 bg-gray-50 opacity-75' : ''
                }`}
              >
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr),18rem,9rem] xl:items-start">
                  <div className="min-w-0 space-y-3">
                    <div>
                      {url.propertyItemId ? (
                        <RouterLink
                          to={`/sources/${sourceId}/properties/${url.propertyItemId}`}
                          className="text-lg font-semibold leading-6 text-gray-900 hover:text-blue-700 hover:underline"
                        >
                          {propertyTitle}
                        </RouterLink>
                      ) : (
                        <h3 className="text-lg font-semibold leading-6 text-gray-500">
                          No linked property
                        </h3>
                      )}
                      <a
                        href={url.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 block break-all text-sm leading-6 text-blue-600 underline hover:text-blue-800"
                        title={url.sourceUrl}
                        onClick={e => e.stopPropagation()}
                      >
                        {url.sourceUrl}
                      </a>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="default" size="sm">
                        {url.urlSource}
                      </Badge>
                      {getStatusBadge(url.status)}
                      {url.active === false && (
                        <Badge variant="warning" size="sm">Disabled</Badge>
                      )}
                      {url.urlSource === 'SITEMAP' && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border border-blue-400 text-blue-700 bg-blue-50"
                          title="This URL was added automatically from the sitemap and cannot be deleted"
                        >
                          Auto
                        </span>
                      )}
                    </div>
                    {url.errorMessage && (
                      <div
                        className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
                        title={url.errorMessage}
                      >
                        Error: {url.errorMessage.substring(0, 180)}
                        {url.errorMessage.length > 180 ? '...' : ''}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3 xl:grid-cols-1">
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Last processed
                      </div>
                      <div className="text-gray-900">
                        {formatDateTime(url.lastProcessed)}
                      </div>
                    </div>
                    <div>
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          Full scrape
                        </span>
                        <button
                          type="button"
                          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors disabled:opacity-60 ${
                            url.fullScrapeRequired
                              ? 'border-yellow-300 bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                              : 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'
                          }`}
                          onClick={() => handleToggleFullScrape(url, !url.fullScrapeRequired)}
                          disabled={fullScrapeUpdating === url.urlHash}
                          title={url.fullScrapeRequired ? 'Clear full scrape request' : 'Request a full scrape for this URL'}
                        >
                          {fullScrapeUpdating === url.urlHash ? (
                            <Spinner size="sm" />
                          ) : url.fullScrapeRequired ? (
                            'Scrape requested'
                          ) : (
                            'Scrape now'
                          )}
                        </button>
                      </div>
                      <div className="text-xs text-gray-500">
                        Last: {formatDateTime(url.lastFullScrapeAt)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 xl:justify-end">
                      <button
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        onClick={() => handleTestUrl(url)}
                        title="Test URL"
                      >
                        <Bug className="h-4 w-4" />
                      </button>
                      <button
                        className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleProcessUrl(url)}
                        disabled={processing === url.urlHash}
                        title="Process URL"
                      >
                        {processing === url.urlHash ? (
                          <Spinner size="sm" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </button>
                      <div title={url.active ? "Disable URL" : "Enable URL"}>
                        <Toggle
                          size="sm"
                          checked={url.active !== false}
                          onChange={() => handleToggleUrlActive(url)}
                        />
                      </div>
                      {url.urlSource === 'MANUAL' ? (
                        <button
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                          onClick={() => handleDeleteClick(url)}
                          title="Delete URL"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          className="p-2 text-red-600 opacity-50 cursor-not-allowed"
                          disabled
                          title="Sitemap URLs cannot be deleted as they are managed automatically and may be re-added during the next sitemap processing job"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                </div>
              </div>
            </article>
            );
          })}
        </div>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete URL"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting && <Spinner size="sm" className="mr-2" />}
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </>
        }
      >
        <p className="text-gray-900">
          Are you sure you want to delete this URL?
        </p>
        <p className="text-sm text-gray-600 mt-2">
          {urlToDelete?.sourceUrl}
        </p>
      </Dialog>

      <Dialog
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        title="Test URL Parsing"
        maxWidth="2xl"
        footer={
          <>
            {!testLoading && !testResult && (
              <Button variant="primary" onClick={runUrlTest}>
                Run Test
              </Button>
            )}
            <Button variant="outline" onClick={() => setTestDialogOpen(false)}>
              Close
            </Button>
          </>
        }
      >
        <p className="text-sm font-medium text-gray-900 mb-4">
          URL: {urlToTest?.sourceUrl}
        </p>

        {!testResult && !testLoading && (
          <Alert variant="info" className="mt-4">
            Click "Run Test" to test parsing this URL without saving to the database.
          </Alert>
        )}

        {testLoading && (
          <div className="flex justify-center my-8">
            <Spinner size="lg" />
          </div>
        )}

        {testResult && (
          <div className="mt-4">
            <Alert
              variant={testResult.success ? 'success' : 'error'}
              className="mb-4"
            >
              {testResult.message}
              {testResult.error && (
                <p className="text-sm mt-2">
                  {testResult.error}
                </p>
              )}
            </Alert>

            {testResult.propertyData && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-900 mb-2">
                  Extracted Property Data:
                </p>
                <Card>
                  <div className="p-4">
                    <JsonView data={testResult.propertyData} />
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default SourceUrlsList;
